import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Tag, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealsCarousel = () => {
  const [deals, setDeals] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const response = await axios.get(`${API}/deals`);
        setDeals(response.data);
      } catch (error) {
        console.error('Error fetching deals:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDeals();
  }, []);

  const nextSlide = useCallback(() => {
    if (deals.length > 0) {
      setCurrentIndex((prev) => (prev + 1) % deals.length);
    }
  }, [deals.length]);

  const prevSlide = () => {
    if (deals.length > 0) {
      setCurrentIndex((prev) => (prev - 1 + deals.length) % deals.length);
    }
  };

  // Auto-rotate every 4 seconds
  useEffect(() => {
    if (deals.length > 1 && !isPaused) {
      const interval = setInterval(nextSlide, 4000);
      return () => clearInterval(interval);
    }
  }, [deals.length, isPaused, nextSlide]);

  if (isLoading) {
    return (
      <div className="bg-gradient-to-r from-[#E07A5F] to-[#c96a52] py-3">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse flex items-center justify-center gap-2">
            <div className="w-4 h-4 bg-white/30 rounded-full"></div>
            <div className="h-4 w-48 bg-white/30 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (deals.length === 0) {
    return null;
  }

  const currentDeal = deals[currentIndex];

  return (
    <div 
      className="bg-gradient-to-r from-[#E07A5F] via-[#e8907a] to-[#E07A5F] py-3 relative overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="deals-carousel"
    >
      {/* Animated background sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1 left-[10%] animate-pulse">
          <Sparkles size={12} className="text-white/20" />
        </div>
        <div className="absolute bottom-1 right-[15%] animate-pulse animation-delay-200">
          <Sparkles size={10} className="text-white/20" />
        </div>
        <div className="absolute top-2 right-[30%] animate-pulse animation-delay-400">
          <Sparkles size={8} className="text-white/20" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between gap-4">
          {/* Left Arrow */}
          {deals.length > 1 && (
            <button
              onClick={prevSlide}
              className="p-1 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
              aria-label="Previous deal"
              data-testid="deals-prev-btn"
            >
              <ChevronLeft size={20} className="text-white" />
            </button>
          )}

          {/* Deal Content */}
          <div className="flex-1 flex items-center justify-center gap-3 min-w-0">
            <Tag size={18} className="text-white flex-shrink-0 hidden sm:block" />
            
            {currentDeal.badge && (
              <span className="bg-white text-[#E07A5F] text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 animate-pulse">
                {currentDeal.badge}
              </span>
            )}
            
            <div className="flex items-center gap-2 min-w-0 text-center sm:text-left">
              <span className="text-white font-bold text-sm md:text-base truncate" data-testid="deal-title">
                {currentDeal.title}
              </span>
              <span className="hidden md:inline text-white/90 text-sm">—</span>
              <span className="hidden md:inline text-white/90 text-sm truncate" data-testid="deal-description">
                {currentDeal.description}
              </span>
            </div>
          </div>

          {/* Right Arrow */}
          {deals.length > 1 && (
            <button
              onClick={nextSlide}
              className="p-1 rounded-full hover:bg-white/20 transition-colors flex-shrink-0"
              aria-label="Next deal"
              data-testid="deals-next-btn"
            >
              <ChevronRight size={20} className="text-white" />
            </button>
          )}
        </div>

        {/* Dots indicator */}
        {deals.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-2">
            {deals.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentIndex ? 'bg-white w-4' : 'bg-white/40'
                }`}
                aria-label={`Go to deal ${index + 1}`}
                data-testid={`deal-dot-${index}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealsCarousel;
